import { useState, useCallback, useRef, useEffect } from 'react';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;

interface UsePhotoZoomPanOptions {
  /** 줌 컨테이너 ref (wheel 이벤트 등록용) */
  containerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  /** 마우스 포인터 위치 기반 줌 (어노테이션 뷰어에서 사용) */
  centerBasedZoom?: boolean;
}

export function usePhotoZoomPan({ containerRef, isOpen, centerBasedZoom = false }: UsePhotoZoomPanOptions) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  const lastPanPoint = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const isZoomed = zoom !== 1;

  // 줌/패닝 리셋
  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // 줌 인
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(MAX_ZOOM, z + 0.25));
  }, []);

  // 줌 아웃
  const handleZoomOut = useCallback(() => {
    setZoom(z => {
      const next = Math.max(MIN_ZOOM, z - 0.25);
      if (next <= 1) {
        setPan({ x: 0, y: 0 });
      }
      return next;
    });
  }, []);

  // 줌 토글 (이미지 클릭용)
  const handleZoomToggle = useCallback(() => {
    if (isZoomed) {
      resetZoom();
    } else {
      setZoom(2);
    }
  }, [isZoomed, resetZoom]);

  // 휠 줌
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const currentZoom = zoomRef.current;
      const delta = -e.deltaY * (centerBasedZoom ? 0.001 : 0.002);
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentZoom + delta * currentZoom));

      if (centerBasedZoom) {
        // 마우스 포인터 위치 기반 줌 (어노테이션)
        const rect = el.getBoundingClientRect();
        const mouseX = e.clientX - rect.left - rect.width / 2;
        const mouseY = e.clientY - rect.top - rect.height / 2;
        const zoomRatio = newZoom / currentZoom;
        setPan(prev => ({
          x: mouseX - (mouseX - prev.x) * zoomRatio,
          y: mouseY - (mouseY - prev.y) * zoomRatio,
        }));
      } else {
        // 단순 줌 (패닝 유지, 줌 아웃 시 리셋)
        if (newZoom <= 1) {
          setPan({ x: 0, y: 0 });
        }
      }

      setZoom(newZoom);
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [isOpen, centerBasedZoom]);

  // PointerEvent 기반 패닝 시작
  const handlePanStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsPanning(true);
    hasDraggedRef.current = false;
    lastPanPoint.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  // 패닝 이동
  const handlePanMove = useCallback((e: React.PointerEvent) => {
    if (!isPanning) return;

    const dx = e.clientX - lastPanPoint.current.x;
    const dy = e.clientY - lastPanPoint.current.y;

    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasDraggedRef.current = true;
    }

    setPan(prev => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }));

    lastPanPoint.current = { x: e.clientX, y: e.clientY };
  }, [isPanning]);

  // 패닝 종료
  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // 이미지 스타일
  const imageTransformStyle = {
    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
    transformOrigin: 'center center' as const,
  };

  return {
    zoom,
    setZoom,
    pan,
    setPan,
    isPanning,
    setIsPanning,
    isZoomed,
    resetZoom,
    handleZoomIn,
    handleZoomOut,
    handleZoomToggle,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
    hasDraggedRef,
    lastPanPoint,
    imageTransformStyle,
  };
}
